import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Editor, { DiffEditor } from '@monaco-editor/react'
import { clsx } from 'clsx'
import { api, ApiError, type FileListing, type FileContent, type GitStatus } from '../lib/api'
import { formatBytes, formatRelative } from '../lib/format'
import { Spinner, Tag, ErrorNote, InfoNote } from '../components/ui/Primitives'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

export function FileManager({ id, caps }: { id: string; caps: Set<string> }) {
  const qc = useQueryClient()
  const [path, setPath] = useState('.')
  const [openFile, setOpenFile] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [showDiff, setShowDiff] = useState(false)
  const [confirmName, setConfirmName] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  const canWrite = caps.has('instance.files.write')

  const listing = useQuery({
    queryKey: ['files', id, path],
    queryFn: () => api.get<FileListing>(`/deployments/${id}/files?path=${encodeURIComponent(path)}`),
  })

  const file = useQuery({
    queryKey: ['file', id, openFile],
    queryFn: () => api.get<FileContent>(`/deployments/${id}/files/content?path=${encodeURIComponent(openFile!)}`),
    enabled: Boolean(openFile),
  })

  const git = useQuery({
    queryKey: ['git', id],
    queryFn: () => api.get<GitStatus>(`/deployments/${id}/git/status`),
  })

  const save = useMutation({
    mutationFn: (v: { confirmName?: string }) =>
      api.post(`/deployments/${id}/files/content`, { path: openFile, content: draft, ...v }),
    onSuccess: () => {
      setConfirmName(null)
      setBanner('Guardado. Reiniciá la instancia si el cambio lo necesita.')
      void qc.invalidateQueries({ queryKey: ['file', id, openFile] })
      void qc.invalidateQueries({ queryKey: ['git', id] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'confirmation_required' && err.expectedName) {
        setConfirmName(err.expectedName)
        return
      }
      setBanner(err instanceof Error ? err.message : 'No se pudo guardar')
    },
  })

  const currentContent = file.data?.content ?? ''
  const dirty = Boolean(openFile) && draft !== currentContent

  function open(p: string) {
    setOpenFile(p)
    setShowDiff(false)
    setBanner(null)
    void api.get<FileContent>(`/deployments/${id}/files/content?path=${encodeURIComponent(p)}`)
      .then((f) => setDraft(f.content))
      .catch(() => setDraft(''))
  }

  return (
    <div className="space-y-3">
      {banner && <InfoNote>{banner}</InfoNote>}

      {git.data?.isRepo && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2.5 text-sm">
          <span className="font-mono">{git.data.branch}</span>
          {git.data.clean
            ? <Tag tone="blue">sin cambios</Tag>
            : <Tag tone="amber">{git.data.files.length} archivo(s) modificado(s)</Tag>}
          {git.data.behind > 0 && <Tag tone="amber">{git.data.behind} commits atrás</Tag>}
          {git.data.lastCommit && (
            <span className="hint ml-auto truncate">
              {git.data.lastCommit.message} · {git.data.lastCommit.author}
            </span>
          )}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[20rem_1fr]">
        <div className="card p-0">
          <div className="flex items-center gap-2 border-b px-4 py-2.5">
            <code className="min-w-0 flex-1 truncate font-mono text-xs">{path}</code>
            {listing.data?.parent !== null && listing.data !== undefined && (
              <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setPath(listing.data!.parent!)}>
                ↑ subir
              </button>
            )}
          </div>

          {listing.isLoading && <div className="p-4"><Spinner /></div>}
          {listing.error && <div className="p-4"><ErrorNote>{(listing.error as Error).message}</ErrorNote></div>}

          <div className="max-h-[32rem] divide-y divide-slate-800/60 overflow-auto">
            {(listing.data?.entries ?? []).map((e) => (
              <button
                key={e.path}
                className={clsx(
                  'flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-800/50',
                  openFile === e.path && 'bg-blue-600/10',
                )}
                disabled={e.escapes}
                onClick={() => (e.isDirectory ? setPath(e.path) : open(e.path))}
                title={e.escapes ? 'Este enlace apunta fuera del despliegue: el panel no lo abre' : undefined}
              >
                <span className="shrink-0 text-slate-500">{e.isDirectory ? '📁' : '📄'}</span>
                <span className={clsx('min-w-0 flex-1 truncate font-mono text-xs', e.escapes && 'text-slate-600 line-through')}>
                  {e.name}
                </span>
                {e.escapes && <Tag tone="amber">fuera</Tag>}
                {!e.isDirectory && <span className="hint shrink-0">{formatBytes(e.size)}</span>}
                <span className="hint shrink-0 font-mono">{e.mode}</span>
              </button>
            ))}
            {listing.data?.entries.length === 0 && <p className="hint px-4 py-6">Directorio vacío.</p>}
          </div>
        </div>

        <div className="card min-w-0 p-0">
          {!openFile ? (
            <p className="hint p-8 text-center">Elegí un archivo de la izquierda para verlo o editarlo.</p>
          ) : file.isLoading ? (
            <div className="p-8"><Spinner /></div>
          ) : file.data?.binary ? (
            <div className="p-8 text-center">
              <p className="hint">Este archivo es binario.</p>
              <a className="btn-ghost mt-3 text-sm" href={`/api/v1/deployments/${id}/files/download?path=${encodeURIComponent(openFile)}`}>
                Descargar
              </a>
            </div>
          ) : file.data?.tooLarge ? (
            <div className="p-8 text-center">
              <p className="hint">Pesa {formatBytes(file.data.size)}, demasiado para el editor.</p>
              <a className="btn-ghost mt-3 text-sm" href={`/api/v1/deployments/${id}/files/download?path=${encodeURIComponent(openFile)}`}>
                Descargar
              </a>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
                <code className="min-w-0 flex-1 truncate font-mono text-xs">{openFile}</code>
                {dirty && <Tag tone="amber">sin guardar</Tag>}
                {file.data?.head !== null && file.data?.head !== undefined && (
                  <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setShowDiff((v) => !v)}>
                    {showDiff ? 'Editar' : 'Ver cambios vs HEAD'}
                  </button>
                )}
                <button
                  className="btn-primary px-3 py-1 text-xs"
                  disabled={!canWrite || !dirty || save.isPending}
                  onClick={() => save.mutate({})}
                  title={canWrite ? undefined : 'Necesitás el permiso instance.files.write'}
                >
                  {save.isPending && <Spinner />} Guardar
                </button>
              </div>

              {showDiff && file.data?.head !== null ? (
                <DiffEditor
                  height="32rem"
                  theme="vs-dark"
                  original={file.data?.head ?? ''}
                  modified={draft}
                  options={{ readOnly: true, renderSideBySide: false, fontSize: 12, minimap: { enabled: false } }}
                />
              ) : (
                <Editor
                  height="32rem"
                  theme="vs-dark"
                  language={languageOf(openFile)}
                  value={draft}
                  onChange={(v) => setDraft(v ?? '')}
                  options={{ readOnly: !canWrite, fontSize: 12, minimap: { enabled: false }, wordWrap: 'on' }}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(confirmName)}
        title="Guardar en una instancia protegida"
        description={`Estás por escribir en "${openFile}" de una instancia marcada como protegida.`}
        confirmName={confirmName}
        confirmLabel="Guardar"
        busy={save.isPending}
        onCancel={() => setConfirmName(null)}
        onConfirm={(typed) => save.mutate({ confirmName: typed })}
      />
    </div>
  )
}

const LANGS: Record<string, string> = {
  ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript',
  json: 'json', md: 'markdown', css: 'css', scss: 'scss', html: 'html',
  php: 'php', py: 'python', go: 'go', rs: 'rust', sql: 'sql',
  yml: 'yaml', yaml: 'yaml', sh: 'shell', env: 'ini', conf: 'ini', ini: 'ini',
}

function languageOf(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? ''
  return LANGS[ext] ?? 'plaintext'
}

export { formatRelative }
